import { centerCrop, makeAspectCrop } from 'react-image-crop';
import type { AspectRatio, PhotoPresentationLayout } from '@shared/utils/photoPresentationLayout';
import { PHOTO_PRESENTATION_CONFIG } from '@shared/utils/photoPresentationLayout';
import type { MediaPhotoWithCropAndAlignmentInfo, MediaPhotoWithCropAndAlignmentInfoWithUnit } from '../HeaderPhotoCustomization.types';
import type { DesignLayoutType } from '@graphql/generated-base';
import { PhotoXPosition, PhotoYPosition } from '@graphql/generated-base';
import type { MediaPhoto } from '../components/PhotoSelector/PhotoSelector.types';
import type { UploadedPreviewImage } from '@shared/components/PreviewWithPhoto/PreviewWithPhoto.types';

export const getPhotoAspectRatioByConfig = (aspectRatio: AspectRatio, assetIndex: number) => {
  return Array.isArray(aspectRatio) ? aspectRatio[assetIndex] : aspectRatio;
};

// Function overloads
export function formatCropInfoUnitValue(photos: MediaPhotoWithCropAndAlignmentInfo[], unitToReplace: 'percent'): MediaPhotoWithCropAndAlignmentInfoWithUnit[];
export function formatCropInfoUnitValue(photos: MediaPhotoWithCropAndAlignmentInfoWithUnit[], unitToReplace: '%'): MediaPhotoWithCropAndAlignmentInfo[];
// Implementation
export function formatCropInfoUnitValue(
  photos: MediaPhotoWithCropAndAlignmentInfo[] | MediaPhotoWithCropAndAlignmentInfoWithUnit[],
  unitToReplace: 'percent' | '%',
): MediaPhotoWithCropAndAlignmentInfoWithUnit[] | MediaPhotoWithCropAndAlignmentInfo[] {
  return photos.map(photo => ({
    ...photo,
    ...(photo.cropInfo && {
      cropInfo: {
        ...photo.cropInfo,
        unit: unitToReplace,
      },
    }),
    ...(photo.mobileCropInfo && {
      mobileCropInfo: {
        ...photo.mobileCropInfo,
        unit: unitToReplace,
      },
    }),
  })) as MediaPhotoWithCropAndAlignmentInfoWithUnit[] | MediaPhotoWithCropAndAlignmentInfo[];
}

const DEFAULT_CROP_WIDTH_PERCENT = 90;

/**
 * Generates photo data for a single image (e.g. uploaded preview), including crop and alignment info,
 * based on the photo presentation layout, design layout type, and slot index.
 */
export const generatePhotoData = (
  photo: UploadedPreviewImage,
  photoPresentationLayout: PhotoPresentationLayout,
  designLayout: DesignLayoutType,
  slotIndex: number = 0,
): MediaPhotoWithCropAndAlignmentInfoWithUnit => {
  const config = PHOTO_PRESENTATION_CONFIG[photoPresentationLayout];
  const aspectRatioFromConfig = config[designLayout]?.aspectRatio || config.aspectRatio;
  const aspectRatio = getPhotoAspectRatioByConfig(aspectRatioFromConfig, slotIndex) || 1;
  return {
    id: photo.id,
    url: photo.url,
    height: photo.height,
    width: photo.width,
    blurHash: photo.blurHash,
    cropInfo:
      aspectRatio && typeof aspectRatio === 'number'
        ? {
            ...centerCrop(makeAspectCrop({ unit: '%', width: DEFAULT_CROP_WIDTH_PERCENT }, aspectRatio, photo.width, photo.height), photo.width, photo.height),
            unit: 'percent',
          }
        : undefined,
    alignment: aspectRatioFromConfig === 'locked' ? { alignX: PhotoXPosition.center, alignY: PhotoYPosition.center } : undefined,
  };
};

/**
 * Generates photo data with center crop applied based on the photo presentation layout
 * and design layout type. Used to auto-populate photos when switching to an empty header type.
 */
export const generatePhotosWithCenterCrop = (
  eventMediaPhotos: readonly MediaPhoto[],
  photoPresentationLayout: PhotoPresentationLayout,
  designLayout: DesignLayoutType,
  photoCount: number,
  startSlotIndex: number = 0,
): MediaPhotoWithCropAndAlignmentInfoWithUnit[] => {
  const config = PHOTO_PRESENTATION_CONFIG[photoPresentationLayout];
  const aspectRatioFromConfig = config[designLayout]?.aspectRatio || config.aspectRatio;

  const photosToUse = eventMediaPhotos.slice(0, Math.min(photoCount, eventMediaPhotos.length));

  return photosToUse.map((mediaPhoto, index) => {
    const photo = mediaPhoto.photo;
    // Use a fallback aspect ratio of 1/1 when no fixed aspect ratio is defined (e.g., for layouts like "marquee" that support freeform cropping)
    const aspectRatio = getPhotoAspectRatioByConfig(aspectRatioFromConfig, startSlotIndex + index) || 1;
    const mobileAspectRatio = config.mobileAspectRatio;
    const photoData: MediaPhotoWithCropAndAlignmentInfoWithUnit = {
      id: mediaPhoto.id,
      url: photo.url,
      height: photo.height,
      width: photo.width,
      blurHash: photo.blurHash ?? undefined,
      cropInfo:
        aspectRatio && typeof aspectRatio === 'number'
          ? {
              ...centerCrop(
                makeAspectCrop(
                  {
                    unit: '%',
                    width: DEFAULT_CROP_WIDTH_PERCENT,
                  },
                  aspectRatio,
                  photo.width,
                  photo.height,
                ),
                photo.width,
                photo.height,
              ),
              unit: 'percent',
            }
          : undefined,
      alignment:
        aspectRatioFromConfig === 'locked'
          ? {
              alignX: PhotoXPosition.center,
              alignY: PhotoYPosition.center,
            }
          : undefined,
      mobileCropInfo: mobileAspectRatio
        ? {
            ...centerCrop(
              makeAspectCrop(
                {
                  unit: '%',
                  width: DEFAULT_CROP_WIDTH_PERCENT,
                },
                mobileAspectRatio,
                photo.width,
                photo.height,
              ),
              photo.width,
              photo.height,
            ),
            unit: 'percent',
          }
        : undefined,
    };

    return photoData;
  });
};
